import { motion } from 'framer-motion';
import { Globe, MapPin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { GeoData, getCountryFlag } from '@/hooks/useQRAnalytics';

interface GeographicAnalysisProps {
  countries: GeoData[];
  cities: GeoData[];
}

export function GeographicAnalysis({ countries, cities }: GeographicAnalysisProps) {
  const maxCountryCount = Math.max(...countries.map(c => c.count), 1);
  const maxCityCount = Math.max(...cities.map(c => c.count), 1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="surface-elevated rounded-xl p-6"
    >
      <div className="flex items-center gap-2 mb-6">
        <Globe className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Geographic Distribution</h3>
      </div>

      <Tabs defaultValue="countries">
        <TabsList className="mb-4">
          <TabsTrigger value="countries">Top Countries</TabsTrigger>
          <TabsTrigger value="cities">Top Cities</TabsTrigger>
        </TabsList>

        <TabsContent value="countries" className="space-y-3">
          {countries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No geographic data available yet</p>
            </div>
          ) : (
            countries.map((country, index) => (
              <motion.div
                key={country.location}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getCountryFlag(country.location)}</span>
                    <span className="font-medium">{country.location}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">{country.count} scans</span>
                    <span className="text-muted-foreground w-12 text-right">{country.percentage}%</span>
                  </div>
                </div>
                <Progress 
                  value={(country.count / maxCountryCount) * 100} 
                  className="h-2"
                />
              </motion.div>
            ))
          )}
        </TabsContent>

        <TabsContent value="cities" className="space-y-3">
          {cities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No city data available yet</p>
            </div>
          ) : (
            cities.map((city, index) => (
              <motion.div
                key={city.location}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{city.location}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">{city.count} scans</span>
                    <span className="text-muted-foreground w-12 text-right">{city.percentage}%</span>
                  </div>
                </div>
                <Progress 
                  value={(city.count / maxCityCount) * 100} 
                  className="h-2"
                />
              </motion.div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
